import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	ApplicationProvider,
	ItemAppLinkApi,
	LinkedAppApi,
} from "../../../../../../../../../services/Api/Organization/types";
import { LinkAppModalFormProps } from "../../../utils/utils";
import GoZoneProduct from "./GoZoneProduct";

interface Props {
	show: boolean;
	onHide: () => void;
	app: LinkedAppApi;
	item: ItemAppLinkApi;
	handleRefresh: () => void;
}

const LinkAppModal = ({ show, onHide, app, item, handleRefresh }: Props) => {
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const form = useForm<LinkAppModalFormProps>({
		criteriaMode: "all",
	});
	const { control, setError, handleSubmit, watch } = form;
	const { addFlash, addSuccessFlash } = useFlash();

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			await apiOrganization.linkApp(
				data.resource_id,
				item.item.id,
				app.app.provider === ApplicationProvider.GOZONE ? data : undefined
			);
			handleRefresh();
			addSuccessFlash(t("lib:common.flash.completed"));
			onHide();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const searchLink = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getAppLinkSearchSelect(app.app.id, search, params, {
			cancelToken: options?.token,
		});
	};

	const isSubmitButtonDisabled = () => {
		if (!watch("resource_id")) return true;
		if (watch("validity_type") === "TODAY" && !watch("valid_to_time")) return true;
		if (watch("validity_type") === "NUMBER_OF_DAYS" && !watch("number_of_days")) return true;
		if (watch("validity_type") === "PERIOD" && (!watch("valid_from") || !watch("valid_to"))) return true;

		return false;
	};

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{`${t("common.action.link")} "${app.app.name}" ${t("common.word.for")} "${
					item.item.name
				}"`}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<FormSelectGroup
					errors={[]}
					name="resource_id"
					loadOptions={searchLink}
					control={control}
					getOptionValue={(option) => option.id}
					isClearable={false}
					label={t("modules.app.field.connection_with.title")}
				/>
				{app.app.provider === "GOZONE" && <GoZoneProduct form={form} />}
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={loading} type="submit" onClick={onSubmit} disabled={isSubmitButtonDisabled()}>
					{t("lib:common.action.save")}
				</ButtonLoading>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default LinkAppModal;
